import { useState, forwardRef, useImperativeHandle } from 'react';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { formatDateTime } from '@/shared/utils/formatDate';

const TransactionDetailModal = forwardRef((props, ref) => {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);
  const [transaction, setTransaction] = useState(null);

  useImperativeHandle(ref, () => ({
    show: (transactionData) => {
      setTransaction(transactionData);
      setShow(true);
    },
    hide: () => {
      setShow(false);
    }
  }));

  const handleClose = () => {
    setShow(false);
    setTransaction(null);
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending':
        return t('status_pending');
      case 'completed':
        return t('status_completed');
      case 'expired':
        return t('status_expired');
      case 'rejected':
        return t('status_rejected');
      default:
        return status;
    }
  };

  const renderDetailContent = () => {
    if (!transaction?.detail) return null;

    const { detail, type, amount, created_at } = transaction;

    // Deposit 明細
    if (type === 'deposit') {
      return (
        <>
          <div className="mb-4">
            <h6>{t('order_time')}</h6>
            <p className="mb-0">{formatDateTime(created_at)}</p>
          </div>

          <div className="mb-4">
            <h6>{t('order_number')}</h6>
            <p className="mb-0">{detail.order_number || '-'}</p>
          </div>

          <div className="mb-4">
            <h6>{t('deposit_amount')}</h6>
            <p className="mb-0">{amount} FCoin</p>
          </div>

          <div className="mb-4">
            <h6>{t('payment_status')}</h6>
            <p className="mb-0">{getStatusText(detail.payment_status)}</p>
          </div>

          <div className="mb-4">
            <h6>{t('nowpayment_id')}</h6>
            <p className="mb-0">{detail.nowpayment_id || '-'}</p>
          </div>

          {detail.payment_url && (
            <div className="mb-4">
              <h6>{t('payment_url')}</h6>
              <a href={detail.payment_url} target="_blank" rel="noopener noreferrer" className="text-primary">
                {detail.payment_url}
              </a>
            </div>
          )}

          {detail.expired_at && (
            <div className="mb-4">
              <h6>{t('expired_at')}</h6>
              <p className="mb-0">{formatDateTime(detail.expired_at)}</p>
            </div>
          )}

          {detail.paid_at && (
            <div className="mb-4">
              <h6>{t('paid_at')}</h6>
              <p className="mb-0">{formatDateTime(detail.paid_at)}</p>
            </div>
          )}
        </>
      );
    }

    // Withdraw 明細
    if (type === 'withdraw') {
      return (
        <>
          <div className="mb-4">
            <h6>{t('order_time')}</h6>
            <p className="mb-0">{formatDateTime(created_at)}</p>
          </div>

          <div className="mb-4">
            <h6>{t('order_number')}</h6>
            <p className="mb-0">{detail.order_number || '-'}</p>
          </div>

          <div className="mb-4">
            <h6>{t('withdraw_amount')}</h6>
            <p className="mb-0">{amount} FCoin</p>
          </div>

          <div className="mb-4">
            <h6>{t('withdraw_status')}</h6>
            <p className="mb-0">{getStatusText(detail.withdraw_status)}</p>
          </div>

          <div className="mb-4">
            <h6>{t('wallet_address')}</h6>
            <p className="mb-0 text-break">{detail.wallet_address || '-'}</p>
          </div>

          {detail.processed_at && (
            <div className="mb-4">
              <h6>{t('processed_at')}</h6>
              <p className="mb-0">{formatDateTime(detail.processed_at)}</p>
            </div>
          )}

          {detail.reject_reason && (
            <div className="mb-4">
              <h6>{t('reject_reason')}</h6>
              <p className="mb-0 text-danger">{detail.reject_reason}</p>
            </div>
          )}
        </>
      );
    }

    return null;
  };

  if (!transaction) return null;

  return (
    <Modal show={show} onHide={handleClose} centered>
      <Modal.Header className="border-bottom pb-3" closeButton>
        <Modal.Title className="fw-bold">{t('transaction_details')}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {renderDetailContent()}
      </Modal.Body>
    </Modal>
  );
});

TransactionDetailModal.displayName = 'TransactionDetailModal';

export default TransactionDetailModal;
